import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';
import { SimpleLineChart, SimpleBarChart, SimplePieChart, SimpleAreaChart } from '../../components/admin/Charts';
import { DateRangePicker } from '../../components/admin/DateRangePicker';
import HierarchyFilter, { HierarchyFilterValues } from '../../components/admin/HierarchyFilter';
import { exportToCSV } from '../../utils/export';
import { getRouteName } from '../../utils/routeNames';
import { formatISTShort, formatISTSplit } from '../../utils/dateFormat';
import ImagePreview from '../../components/admin/ImagePreview';

// Ant Design imports
import {
  Card,
  Typography,
  Button,
  Alert,
  Tag,
  Avatar,
  Skeleton,
  Row,
  Col,
  Table,
  Flex,
  Empty,
  Rate,
  Select,
  Modal,
  Collapse,
} from 'antd';
import type { TableColumnsType } from 'antd';

// Ant Design Icons
import {
  ApiOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  StarOutlined,
  LikeOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

// Helper function to format time
const formatDuration = (ms: number | undefined | null): string => {
  if (typeof ms !== 'number' || isNaN(ms)) return 'N/A';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms.toFixed(0)}ms`;
};

type AnalyticsTab = 'cache' | 'search' | 'api' | 'errors' | 'logs' | 'performance' | 'feedback' | 'billing';

interface AnalyticsProps {
  initialTab?: AnalyticsTab;
}

function Analytics({ initialTab = 'cache' }: AnalyticsProps) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [hierarchyFilter, setHierarchyFilter] = useState<HierarchyFilterValues>({
    tenantId: null,
    businessId: null,
    applicationId: null,
  });

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleLastUpdated = useCallback((ts: Date) => setLastUpdated(ts), []);

  const { tenantId, businessId, applicationId } = hierarchyFilter;

  const renderContent = () => {
    const common = { startDate, endDate, tenantId, businessId, applicationId, onLastUpdated: handleLastUpdated };
    switch (initialTab) {
      case 'cache':
        return <CacheStatisticsTab {...common} />;
      case 'search':
        return <SearchAnalyticsTab {...common} />;
      case 'api':
        return <APIUsageTab {...common} />;
      case 'errors':
        return <ErrorAnalyticsTab {...common} />;
      case 'performance':
        return <PerformanceMetricsTab {...common} />;
      case 'feedback':
        return <FeedbackStatsTab {...common} />;
      case 'billing':
        return <BillingTab {...common} />;
      default:
        return <CacheStatisticsTab {...common} />;
    }
  };

  return (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
        {initialTab !== 'cache' && <HierarchyFilter value={hierarchyFilter} onChange={setHierarchyFilter} />}
        <Flex align="center" gap={16}>
          <LastUpdated timestamp={lastUpdated} />
          <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} />
        </Flex>
      </Flex>
      {renderContent()}
    </div>
  );
}

interface TabFilterProps {
  startDate: Date | null;
  endDate: Date | null;
  tenantId: string | null;
  businessId: string | null;
  applicationId: string | null;
  onLastUpdated: (ts: Date) => void;
}

// ─── Last Updated Indicator ─────────────────────────────────────────
const LastUpdated: React.FC<{ timestamp: Date | null }> = ({ timestamp }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(iv);
  }, []);
  if (!timestamp) return null;
  const secs = Math.floor((Date.now() - timestamp.getTime()) / 1000);
  const label = secs < 10 ? 'just now' : secs < 60 ? `${secs}s ago` : `${Math.floor(secs / 60)}m ago`;
  return (
    <Flex align="center" gap={6}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#52c41a', display: 'inline-block', animation: 'pulse 2s infinite' }} />
      <Text type="secondary" style={{ fontSize: 12 }}>Updated {label}</Text>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </Flex>
  );
};

// ─── Shared Stat Card Style ─────────────────────────────────────────
const statCardBodyStyle: React.CSSProperties = { textAlign: 'center', minHeight: 110, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' };

// ─── Empty State Helper ─────────────────────────────────────────────
const EmptyState = ({ description = 'No data available — try adjusting your date range or filters' }: { description?: string }) => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">{description}</Text>} />
);

// ═══════════════════════════════════════════════════════════════════
// Cache Statistics Tab
// ═══════════════════════════════════════════════════════════════════
function CacheStatisticsTab({ onLastUpdated }: TabFilterProps) {
  const [cacheStats, setCacheStats] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const loadCacheStats = useCallback(async () => {
    try {
      const url = `${getBackendUrl()}/api/analytics/cache-stats`;
      const response = await adminFetch(url);
      if (response.ok) {
        const data = await response.json();
        setCacheStats(data.data || { enabled: false, hit_rate: 0.0, hits: 0, misses: 0, total_requests: 0 });
        setError('');
        onLastUpdated(new Date());
      } else {
        setCacheStats({ enabled: false, hit_rate: 0.0, hits: 0, misses: 0, total_requests: 0 });
      }
    } catch (err: any) {
      setCacheStats({ enabled: false, hit_rate: 0.0, hits: 0, misses: 0, total_requests: 0 });
    }
  }, [onLastUpdated]);

  useEffect(() => {
    loadCacheStats();
    const interval = setInterval(loadCacheStats, 30000);
    return () => clearInterval(interval);
  }, [loadCacheStats]);

  if (!cacheStats && !error) {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Col xs={24} sm={12} md={4} key={i}>
            <Skeleton active paragraph={{ rows: 2 }} />
          </Col>
        ))}
      </Row>
    );
  }

  const formatMemory = (bytes: number | undefined) => {
    if (!bytes) return 'N/A';
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  return (
    <div>
      {error && <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />}
      {cacheStats?.error && <Alert message={`Cache may not be available: ${cacheStats.error}`} type="warning" style={{ marginBottom: 16 }} showIcon />}

      <Card>
        <Title level={5} style={{ marginBottom: 16 }}>Cache Performance</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={4}>
            <Card size="small" styles={{ body: statCardBodyStyle }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Hit Rate</Text>
              <Title level={3} style={{ margin: '4px 0 0' }}>
                {cacheStats?.hit_rate !== undefined && cacheStats?.hit_rate !== null ? `${cacheStats.hit_rate}%` : '0.0%'}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small" styles={{ body: statCardBodyStyle }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Cache Hits</Text>
              <Title level={3} style={{ margin: '4px 0 0', color: '#52c41a' }}>{(cacheStats?.hits || 0).toLocaleString()}</Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small" styles={{ body: statCardBodyStyle }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Cache Misses</Text>
              <Title level={3} style={{ margin: '4px 0 0', color: '#faad14' }}>{(cacheStats?.misses || 0).toLocaleString()}</Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small" styles={{ body: statCardBodyStyle }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Total Requests</Text>
              <Title level={3} style={{ margin: '4px 0 0' }}>{(cacheStats?.total_requests || 0).toLocaleString()}</Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small" styles={{ body: statCardBodyStyle }}>
              <Avatar icon={<DatabaseOutlined />} size="small" style={{ backgroundColor: '#722ed1', marginBottom: 4 }} />
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Total Keys</Text>
              <Title level={3} style={{ margin: '4px 0 0' }}>{(cacheStats?.total_keys || 0).toLocaleString()}</Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small" styles={{ body: statCardBodyStyle }}>
              <Avatar icon={<CloudServerOutlined />} size="small" style={{ backgroundColor: '#13c2c2', marginBottom: 4 }} />
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Memory Usage</Text>
              <Title level={3} style={{ margin: '4px 0 0' }}>{formatMemory(cacheStats?.memory_usage)}</Title>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Search Analytics Tab
// ═══════════════════════════════════════════════════════════════════
function SearchAnalyticsTab({ startDate, endDate, tenantId, businessId, applicationId, onLastUpdated }: TabFilterProps) {
  const [searchStats, setSearchStats] = useState<any>(null);
  const [popularSearches, setPopularSearches] = useState<any[]>([]);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTypeFilter, setSearchTypeFilter] = useState<string | undefined>(undefined);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImageIds, setPreviewImageIds] = useState<string[]>([]);

  // New state for aggregated analytics
  const [topParts, setTopParts] = useState<any[]>([]);
  const [topMakes, setTopMakes] = useState<any[]>([]);
  const [topModels, setTopModels] = useState<any[]>([]);
  const [topAggregates, setTopAggregates] = useState<any[]>([]);
  const [zeroResults, setZeroResults] = useState<any>(null);
  const [trends, setTrends] = useState<any[]>([]);
  const [trendField, setTrendField] = useState<string>('make');
  const [trendGranularity, setTrendGranularity] = useState<string>('daily');
  const [zeroGroupBy, setZeroGroupBy] = useState<string>('part_description');

  const loadSearchAnalytics = useCallback(async () => {
    try {
      setError('');

      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (tenantId) params.append('tenant_id', tenantId);
      if (businessId) params.append('business_id', businessId);
      if (applicationId) params.append('application_id', applicationId);

      // Fetch all data in parallel
      const [statsRes, popularRes, partsRes, makesRes, modelsRes, aggRes, zeroRes, trendsRes] = await Promise.all([
        adminFetch(`${getBackendUrl()}/api/analytics/stats/search?${params}`),
        adminFetch(`${getBackendUrl()}/api/analytics/stats/popular-searches?limit=50&${searchTypeFilter ? `search_type=${searchTypeFilter}&` : ''}${params}`),
        adminFetch(`${getBackendUrl()}/api/analytics/stats/top-parts?limit=10&${params}`),
        adminFetch(`${getBackendUrl()}/api/analytics/stats/top-makes?limit=10&${params}`),
        adminFetch(`${getBackendUrl()}/api/analytics/stats/top-models?limit=10&${params}`),
        adminFetch(`${getBackendUrl()}/api/analytics/stats/top-aggregates?limit=10&${params}`),
        adminFetch(`${getBackendUrl()}/api/analytics/stats/zero-results?group_by=${zeroGroupBy}&limit=20&${params}`),
        adminFetch(`${getBackendUrl()}/api/analytics/stats/trends?field=${trendField}&granularity=${trendGranularity}&top_n=5&${params}`),
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setSearchStats(statsData.data || { stats: [], summary: {} });
      }
      if (popularRes.ok) {
        const popularData = await popularRes.json();
        setPopularSearches(popularData.data || []);
      }
      if (partsRes.ok) {
        const partsData = await partsRes.json();
        setTopParts(partsData.data || []);
      }
      if (makesRes.ok) {
        const makesData = await makesRes.json();
        setTopMakes(makesData.data || []);
      }
      if (modelsRes.ok) {
        const modelsData = await modelsRes.json();
        setTopModels(modelsData.data || []);
      }
      if (aggRes.ok) {
        const aggData = await aggRes.json();
        setTopAggregates(aggData.data || []);
      }
      if (zeroRes.ok) {
        const zeroData = await zeroRes.json();
        setZeroResults(zeroData.data || { total: 0, items: [] });
      }
      if (trendsRes.ok) {
        const trendsData = await trendsRes.json();
        setTrends(trendsData.data || []);
      }

      onLastUpdated(new Date());
    } catch (err: any) {
      setError(`Error: ${err.message || 'Failed to load search analytics'}`);
    }
  }, [startDate, endDate, tenantId, businessId, applicationId, searchTypeFilter, trendField, trendGranularity, zeroGroupBy, onLastUpdated]);

  useEffect(() => {
    loadSearchAnalytics();
    const interval = setInterval(loadSearchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [loadSearchAnalytics]);

  const searchTrendsData = searchStats?.stats?.map((stat: any) => ({
    name: stat.date ? formatISTShort(stat.date) : 'Unknown',
    searches: stat.total_searches || 0,
  })) || [];

  const searchTypeData = searchStats?.stats?.reduce((acc: any, stat: any) => {
    const type = stat.search_type || 'unknown';
    acc[type] = (acc[type] || 0) + (stat.total_searches || 0);
    return acc;
  }, {}) || {};

  const searchTypeChartData = Object.entries(searchTypeData).map(([name, value]) => ({
    name,
    value: typeof value === 'number' ? value : 0,
  }));

  const typeColorMap: Record<string, string> = { TEXT: 'blue', IMAGE: 'orange', ASR: 'green', VALIDATION: 'red', HYBRID: 'purple', BULK: 'cyan', SUGGESTION: 'geekblue' };

  // Column definitions for aggregated tables - with proper widths to prevent wrapping
  const topFieldColumns = (fieldName: string, dataKey: string): TableColumnsType<any> => [
    { title: fieldName, dataIndex: dataKey, key: 'field', ellipsis: true, render: (v: string) => <Text strong>{v || 'N/A'}</Text> },
    { title: 'Searches', dataIndex: 'search_count', key: 'search_count', width: 100, align: 'right' as const, sorter: (a: any, b: any) => (a.search_count || 0) - (b.search_count || 0), render: (v: number) => <Text>{(v || 0).toLocaleString()}</Text> },
    { title: 'Avg Results', dataIndex: 'avg_results', key: 'avg_results', width: 110, align: 'right' as const, render: (v: number) => typeof v === 'number' ? v.toFixed(0) : '-' },
    { title: 'Zero Results', dataIndex: 'zero_result_count', key: 'zero_result_count', width: 110, align: 'right' as const, render: (v: number) => v > 0 ? <Text type="warning" strong>{v}</Text> : <Text type="secondary">0</Text> },
  ];

  const zeroResultColumns: TableColumnsType<any> = [
    { title: zeroGroupBy === 'part_description' ? 'Part' : zeroGroupBy.charAt(0).toUpperCase() + zeroGroupBy.slice(1), dataIndex: 'field_value', key: 'field_value', ellipsis: true, render: (v: string) => <Text strong>{v || 'N/A'}</Text> },
    { title: 'Requests', dataIndex: 'count', key: 'count', width: 100, align: 'right' as const, sorter: (a: any, b: any) => (a.count || 0) - (b.count || 0), render: (v: number) => <Text type="warning" strong>{(v || 0).toLocaleString()}</Text> },
    { title: 'First Searched', dataIndex: 'first_occurrence', key: 'first_occurrence', width: 130, align: 'center' as const, render: (v: string) => v ? <Text type="secondary">{formatISTShort(v)}</Text> : '-' },
    { title: 'Last Searched', dataIndex: 'last_occurrence', key: 'last_occurrence', width: 130, align: 'center' as const, render: (v: string) => v ? <Text type="secondary">{formatISTShort(v)}</Text> : '-' },
  ];

  const popularColumns: TableColumnsType<any> = [
    { title: 'Query', dataIndex: 'query', key: 'query', ellipsis: true, render: (v: string) => v || 'N/A' },
    { title: 'Type', dataIndex: 'search_type', key: 'search_type', width: 120, render: (v: string, record: any) => {
      const tag = <Tag color={typeColorMap[v] || 'default'}>{v || 'N/A'}</Tag>;
      if ((v === 'IMAGE' || v === 'HYBRID') && record.image_path) {
        return (
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => { setPreviewImageIds(record.image_path.split(',').map((id: string) => id.trim())); setPreviewModalOpen(true); }}
          >
            {tag}
          </span>
        );
      }
      return tag;
    }},
    { title: 'Count', dataIndex: 'count', key: 'count', width: 80, sorter: (a: any, b: any) => (a.count || 0) - (b.count || 0) },
    { title: 'Avg Response', dataIndex: 'avg_response_time', key: 'avg_response_time', width: 120, render: (v: number) => formatDuration(v) },
    { title: 'Avg Results', dataIndex: 'avg_results', key: 'avg_results', width: 100, render: (v: number) => typeof v === 'number' ? v.toFixed(0) : 'N/A' },
  ];

  // Transform trends data for multi-line chart
  const trendChartData = trends.map((item: any) => ({
    name: item.date || 'Unknown',
    ...Object.fromEntries(Object.entries(item).filter(([k]) => k !== 'date')),
  }));

  return (
    <div>
      {error && <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />}

      {/* Summary Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Searches</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{(searchStats?.summary?.total_searches || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Avg Response Time</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{formatDuration(searchStats?.summary?.avg_response_time)}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: '#1890ff', marginBottom: 4 }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Unique Sessions</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{(searchStats?.summary?.unique_sessions || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Zero-Result Searches</Text>
            <Title level={3} style={{ margin: '4px 0 0', color: '#faad14' }}>{(zeroResults?.total || 0).toLocaleString()}</Title>
          </Card>
        </Col>
      </Row>

      {/* Search Trends Chart with field/granularity selectors */}
      <Card size="small" title="Search Trends" extra={
        <Flex gap={8}>
          <Select size="small" value={trendField} onChange={setTrendField} style={{ width: 140 }} options={[
            { label: 'By Make', value: 'make' },
            { label: 'By Model', value: 'model' },
            { label: 'By Part', value: 'part_description' },
            { label: 'By Category', value: 'aggregate' },
            { label: 'By Fuel Type', value: 'fuel_type' },
          ]} />
          <Select size="small" value={trendGranularity} onChange={setTrendGranularity} style={{ width: 100 }} options={[
            { label: 'Daily', value: 'daily' },
            { label: 'Weekly', value: 'weekly' },
            { label: 'Monthly', value: 'monthly' },
          ]} />
        </Flex>
      } style={{ marginBottom: 16 }}>
        {trendChartData.length > 0 ? (
          <SimpleLineChart data={trendChartData} dataKey={Object.keys(trendChartData[0] || {}).find(k => k !== 'name') || 'value'} title="" height={300} />
        ) : (
          <EmptyState description="No trend data available yet. Trends will appear after searches are made." />
        )}
      </Card>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small">
            {searchTrendsData.length > 0
              ? <SimpleLineChart data={searchTrendsData} dataKey="searches" title="Search Volume Over Time" height={300} />
              : <EmptyState />}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small">
            {searchTypeChartData.length > 0
              ? <SimplePieChart data={searchTypeChartData} dataKey="value" title="Search Type Breakdown" height={300} onSliceClick={(name) => { setSearchTypeFilter(name === searchTypeFilter ? undefined : name); setPage(1); }} />
              : <EmptyState />}
          </Card>
        </Col>
      </Row>

      {/* Aggregated Analytics Grid */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="Top Searched Parts" extra={<Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(topParts, 'top-parts')}>Export</Button>}>
            <Table
              columns={topFieldColumns('Part', 'part_description')}
              dataSource={topParts}
              rowKey={(r, i) => `part-${i}`}
              size="small"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              locale={{ emptyText: <EmptyState description="No data yet. Parts will appear after searches." /> }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="Top Searched Makes" extra={<Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(topMakes, 'top-makes')}>Export</Button>}>
            <Table
              columns={topFieldColumns('Make', 'make')}
              dataSource={topMakes}
              rowKey={(r, i) => `make-${i}`}
              size="small"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              locale={{ emptyText: <EmptyState description="No data yet. Makes will appear after searches." /> }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="Top Searched Models" extra={<Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(topModels, 'top-models')}>Export</Button>}>
            <Table
              columns={topFieldColumns('Model', 'model')}
              dataSource={topModels}
              rowKey={(r, i) => `model-${i}`}
              size="small"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              locale={{ emptyText: <EmptyState description="No data yet. Models will appear after searches." /> }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="Top Categories" extra={<Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(topAggregates, 'top-categories')}>Export</Button>}>
            <Table
              columns={topFieldColumns('Category', 'aggregate')}
              dataSource={topAggregates}
              rowKey={(r, i) => `agg-${i}`}
              size="small"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              locale={{ emptyText: <EmptyState description="No data yet. Categories will appear after searches." /> }}
            />
          </Card>
        </Col>
      </Row>

      {/* Zero-Result Searches Section */}
      <Card size="small" title="Zero-Result Searches (Inventory Gaps)" extra={
        <Flex gap={8} align="center">
          <Select size="small" value={zeroGroupBy} onChange={setZeroGroupBy} style={{ width: 130 }} options={[
            { label: 'By Part', value: 'part_description' },
            { label: 'By Make', value: 'make' },
            { label: 'By Model', value: 'model' },
            { label: 'By Category', value: 'aggregate' },
          ]} />
          <Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(zeroResults?.items || [], 'zero-result-searches')}>Export</Button>
        </Flex>
      } style={{ marginBottom: 16 }}>
        {zeroResults?.total > 0 && (
          <Alert message={`${zeroResults.total} searches returned no results - these represent potential inventory gaps`} type="warning" showIcon style={{ marginBottom: 12 }} />
        )}
        <Table
          columns={zeroResultColumns}
          dataSource={zeroResults?.items || []}
          rowKey={(r, i) => `zero-${i}`}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          locale={{ emptyText: <EmptyState description="No zero-result searches recorded yet." /> }}
        />
      </Card>

      {/* Raw Queries (Collapsible) */}
      <Collapse
        items={[{
          key: 'raw-queries',
          label: 'Raw Search Queries',
          children: (
            <>
              <Flex gap={8} align="center" style={{ marginBottom: 12 }}>
                <Select
                  size="small"
                  placeholder="All Types"
                  allowClear
                  value={searchTypeFilter}
                  onChange={(v) => { setSearchTypeFilter(v); setPage(1); }}
                  style={{ width: 130 }}
                  options={[
                    { label: 'Text', value: 'TEXT' },
                    { label: 'Image', value: 'IMAGE' },
                    { label: 'ASR', value: 'ASR' },
                    { label: 'Validation', value: 'VALIDATION' },
                    { label: 'Hybrid', value: 'HYBRID' },
                  ]}
                />
                <Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(popularSearches, 'popular-searches')}>Export</Button>
              </Flex>
              <Table
                columns={popularColumns}
                dataSource={popularSearches}
                rowKey={(r, i) => `${r.query}-${i}`}
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{
                  current: page,
                  pageSize,
                  onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
                locale={{ emptyText: <EmptyState /> }}
              />
            </>
          ),
        }]}
        style={{ marginBottom: 16 }}
      />

      {/* Image Preview Modal */}
      <Modal
        open={previewModalOpen}
        onCancel={() => setPreviewModalOpen(false)}
        footer={null}
        title="Uploaded Image(s)"
        width={520}
        centered
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', padding: '12px 0' }}>
          {previewImageIds.map((id) => (
            <ImagePreview key={id} imageId={id} size={200} />
          ))}
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// API Usage Tab
// ═══════════════════════════════════════════════════════════════════
function APIUsageTab({ startDate, endDate, tenantId, businessId, applicationId, onLastUpdated }: TabFilterProps) {
  const [apiStats, setApiStats] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const loadAPIStats = useCallback(async () => {
    try {
      setError('');

      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (tenantId) params.append('tenant_id', tenantId);
      if (businessId) params.append('business_id', businessId);
      if (applicationId) params.append('application_id', applicationId);

      const response = await adminFetch(`${getBackendUrl()}/api/analytics/stats/api?${params}`);
      if (response.ok) {
        const data = await response.json();
        setApiStats(data.data || { endpoints: [], summary: {} });
        onLastUpdated(new Date());
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setError(`Failed to load API usage: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(`Error: ${err.message || 'Failed to load API usage statistics'}`);
    }
  }, [startDate, endDate, tenantId, businessId, applicationId, onLastUpdated]);

  useEffect(() => {
    loadAPIStats();
    const interval = setInterval(loadAPIStats, 30000);
    return () => clearInterval(interval);
  }, [loadAPIStats]);

  const endpointData = apiStats?.endpoints?.slice(0, 10).map((ep: any) => ({
    name: getRouteName(ep.route) || `${ep.method} ${ep.route}`.substring(0, 30),
    requests: ep.total_requests || 0,
  })) || [];

  const endpointColumns: TableColumnsType<any> = [
    { title: 'Route', dataIndex: 'route', key: 'route', ellipsis: true, render: (v: string) => getRouteName(v) },
    { title: 'Method', dataIndex: 'method', key: 'method', width: 80, render: (v: string) => <Tag color={v === 'GET' ? 'blue' : v === 'POST' ? 'green' : 'default'}>{v}</Tag> },
    { title: 'Requests', dataIndex: 'total_requests', key: 'total_requests', width: 100, sorter: (a: any, b: any) => (a.total_requests || 0) - (b.total_requests || 0) },
    { title: 'Success', dataIndex: 'success_count', key: 'success_count', width: 80 },
    { title: 'Errors', dataIndex: 'error_count', key: 'error_count', width: 80, render: (v: number) => v > 0 ? <Text type="danger">{v}</Text> : 0 },
    { title: 'Avg Duration', dataIndex: 'avg_duration', key: 'avg_duration', width: 110, render: (v: number) => formatDuration(v) },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Requests</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{(apiStats?.summary?.total_requests || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Success Rate</Text>
            <Title level={3} style={{ margin: '4px 0 0', color: '#52c41a' }}>
              {typeof apiStats?.summary?.success_rate === 'number' ? `${apiStats.summary.success_rate.toFixed(1)}%` : '0%'}
            </Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Errors</Text>
            <Title level={3} style={{ margin: '4px 0 0', color: '#ff4d4f' }}>{(apiStats?.summary?.total_errors || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Avg Duration</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{formatDuration(apiStats?.summary?.avg_duration)}</Title>
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        {endpointData.length > 0
          ? <SimpleBarChart data={endpointData} dataKey="requests" title="Top Endpoints by Request Count" height={400} />
          : <EmptyState />}
      </Card>

      <Card size="small" title="Endpoint Details" extra={
        <Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(apiStats?.endpoints || [], 'api-usage')}>Export</Button>
      }>
        <Table
          columns={endpointColumns}
          dataSource={apiStats?.endpoints || []}
          rowKey={(r, i) => `${r.route}-${r.method}-${i}`}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          locale={{ emptyText: <EmptyState /> }}
        />
      </Card>
    </div>
  );
}

// ─── Row Highlighting Helper ────────────────────────────────────────
const getRowStyle = (record: any) => {
  const code = record.status_code || record.statusCode;
  if (code >= 500) return { style: { backgroundColor: 'rgba(255,77,79,0.04)' } };
  if (code >= 400) return { style: { backgroundColor: 'rgba(250,173,20,0.04)' } };
  return {};
};

// ═══════════════════════════════════════════════════════════════════
// Error Analytics Tab
// ═══════════════════════════════════════════════════════════════════
function ErrorAnalyticsTab({ startDate, endDate, tenantId, businessId, applicationId, onLastUpdated }: TabFilterProps) {
  const [errorStats, setErrorStats] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const loadErrorStats = useCallback(async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (tenantId) params.append('tenant_id', tenantId);
      if (businessId) params.append('business_id', businessId);
      if (applicationId) params.append('application_id', applicationId);

      const response = await adminFetch(`${getBackendUrl()}/api/analytics/stats/errors?${params}`);
      if (response.ok) {
        const data = await response.json();
        setErrorStats(data.data || { errors: [], error_trends: [], error_types: {}, summary: {} });
        onLastUpdated(new Date());
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setError(`Failed to load error statistics: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(`Error: ${err.message || 'Failed to load error statistics'}`);
    }
  }, [startDate, endDate, tenantId, businessId, applicationId, onLastUpdated]);

  useEffect(() => {
    loadErrorStats();
    const interval = setInterval(loadErrorStats, 30000);
    return () => clearInterval(interval);
  }, [loadErrorStats]);

  const handleStatusCodeClick = (statusCode: number, route: string) => {
    sessionStorage.setItem('errorDrillDown', JSON.stringify({ statusCode, route }));
    window.dispatchEvent(new CustomEvent('navigateToPage', { detail: 'logs-events' }));
  };

  const errorTrendsData = errorStats?.error_trends?.map((trend: any) => ({
    name: trend.date ? formatISTShort(trend.date) : 'Unknown',
    errors: trend.error_count || 0,
    rate: trend.error_rate || 0,
  })) || [];

  const errorTypeData = Object.entries(errorStats?.error_types || {}).map(([name, value]) => ({
    name,
    value: typeof value === 'number' ? value : 0,
  }));

  const errorColumns: TableColumnsType<any> = [
    {
      title: 'Status Code', dataIndex: 'status_code', key: 'status_code', width: 120,
      render: (v: number, record: any) => (
        <Tag color={v >= 500 ? 'red' : v >= 400 ? 'orange' : 'green'}
          style={{ cursor: 'pointer' }}
          onClick={() => handleStatusCodeClick(v, record.route)}
        >{v}</Tag>
      ),
    },
    { title: 'Route', dataIndex: 'route', key: 'route', ellipsis: true, render: (v: string) => getRouteName(v) },
    { title: 'Count', dataIndex: 'error_count', key: 'error_count', width: 80, sorter: (a: any, b: any) => (a.error_count || 0) - (b.error_count || 0) },
    { title: 'Avg Duration', dataIndex: 'avg_duration', key: 'avg_duration', width: 110, render: (v: number) => formatDuration(v) },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Errors</Text>
            <Title level={3} style={{ margin: '4px 0 0', color: '#ff4d4f' }}>{(errorStats?.summary?.total_errors || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Most Common Error</Text>
            <Title level={4} style={{ margin: '4px 0 0' }}>{errorStats?.summary?.most_common_error || 'N/A'}</Title>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small">
            {errorTrendsData.length > 0
              ? <SimpleAreaChart data={errorTrendsData} dataKey="errors" title="Error Rate Trend" height={300} color="#ef4444" />
              : <EmptyState />}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small">
            {errorTypeData.length > 0
              ? <SimplePieChart data={errorTypeData} dataKey="value" title="Error Types Breakdown" height={300} />
              : <EmptyState />}
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Error Details" extra={
        <Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(errorStats?.errors || [], 'errors')}>Export</Button>
      }>
        <Table
          columns={errorColumns}
          dataSource={errorStats?.errors || []}
          rowKey={(r, i) => `${r.status_code}-${r.route}-${i}`}
          size="small"
          scroll={{ x: 'max-content' }}
          onRow={getRowStyle}
          pagination={{
            current: page,
            pageSize,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          locale={{ emptyText: <EmptyState /> }}
        />
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Performance Metrics Tab
// ═══════════════════════════════════════════════════════════════════
function PerformanceMetricsTab({ startDate, endDate, tenantId, businessId, applicationId, onLastUpdated }: TabFilterProps) {
  const [perfStats, setPerfStats] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const loadPerfStats = useCallback(async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (tenantId) params.append('tenant_id', tenantId);
      if (businessId) params.append('business_id', businessId);
      if (applicationId) params.append('application_id', applicationId);

      const response = await adminFetch(`${getBackendUrl()}/api/analytics/stats/performance?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPerfStats(data.data || {});
        onLastUpdated(new Date());
      } else {
        setError('Failed to load performance metrics');
      }
    } catch (err: any) {
      setError(`Error: ${err.message || 'Failed to load performance metrics'}`);
    }
  }, [startDate, endDate, tenantId, businessId, applicationId, onLastUpdated]);

  useEffect(() => {
    loadPerfStats();
    const interval = setInterval(loadPerfStats, 30000);
    return () => clearInterval(interval);
  }, [loadPerfStats]);

  if (!perfStats && !error) {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Col xs={24} sm={12} md={4} key={i}><Skeleton active paragraph={{ rows: 2 }} /></Col>
        ))}
      </Row>
    );
  }

  // Backend returns: { response_times: { avg, min, max, p95, p99 }, throughput: [{ hour, requests }], total_requests }
  const rt = perfStats?.response_times || {};
  const throughputData = (perfStats?.throughput || []).map((t: any) => ({
    name: t.hour ? formatISTShort(t.hour) : 'Unknown',
    requests: t.requests || 0,
  }));

  return (
    <div>
      {error && <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={4}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Avatar icon={<ClockCircleOutlined />} size="small" style={{ backgroundColor: '#1890ff', marginBottom: 4 }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Avg Response</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{formatDuration(rt.avg)}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Avatar icon={<ArrowDownOutlined />} size="small" style={{ backgroundColor: '#52c41a', marginBottom: 4 }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Min Response</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{formatDuration(rt.min)}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Avatar icon={<ArrowUpOutlined />} size="small" style={{ backgroundColor: '#ff4d4f', marginBottom: 4 }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Max Response</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{formatDuration(rt.max)}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>P95</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{formatDuration(rt.p95)}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>P99</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{formatDuration(rt.p99)}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Avatar icon={<ThunderboltOutlined />} size="small" style={{ backgroundColor: '#722ed1', marginBottom: 4 }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Total Requests</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{(perfStats?.total_requests || 0).toLocaleString()}</Title>
          </Card>
        </Col>
      </Row>

      <Card size="small">
        {throughputData.length > 0
          ? <SimpleLineChart data={throughputData} dataKey="requests" title="Throughput (Requests/Hour)" height={300} color="#1890ff" />
          : <EmptyState />}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Feedback Stats Tab
// ═══════════════════════════════════════════════════════════════════
function FeedbackStatsTab({ startDate, endDate, tenantId, businessId, applicationId, onLastUpdated }: TabFilterProps) {
  const [feedbackStats, setFeedbackStats] = useState<any>(null);
  const [feedbackList, setFeedbackList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const loadFeedback = useCallback(async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      if (tenantId) params.append('tenant_id', tenantId);
      if (businessId) params.append('business_id', businessId);
      if (applicationId) params.append('application_id', applicationId);

      // Load stats summary
      const statsRes = await adminFetch(`${getBackendUrl()}/api/feedback/stats?${params}`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setFeedbackStats(statsData.data || {});
      }

      // Load feedback list
      const listParams = new URLSearchParams(params);
      listParams.append('page', String(page));
      listParams.append('page_size', String(pageSize));
      const listRes = await adminFetch(`${getBackendUrl()}/api/feedback/list?${listParams}`);
      if (listRes.ok) {
        const listData = await listRes.json();
        setFeedbackList(listData.data || []);
        setTotal(listData.total || listData.data?.length || 0);
      }
      onLastUpdated(new Date());
    } catch (err: any) {
      setError(`Error: ${err.message || 'Failed to load feedback'}`);
    }
  }, [startDate, endDate, tenantId, businessId, applicationId, page, pageSize, onLastUpdated]);

  useEffect(() => {
    loadFeedback();
    const interval = setInterval(loadFeedback, 30000);
    return () => clearInterval(interval);
  }, [loadFeedback]);

  if (!feedbackStats && !error) {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2, 3].map((i) => (
          <Col xs={24} sm={8} key={i}><Skeleton active paragraph={{ rows: 2 }} /></Col>
        ))}
      </Row>
    );
  }

  const ratingDistribution = (feedbackStats?.rating_distribution || []).map((r: any) => ({
    name: `${r.rating} Star${r.rating !== 1 ? 's' : ''}`,
    value: r.count || 0,
  }));

  const feedbackColumns: TableColumnsType<any> = [
    {
      title: 'Time', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (v: string) => {
        const { date, time } = formatISTSplit(v);
        return <div style={{ lineHeight: 1.3 }}><div style={{ fontWeight: 500, fontSize: 13 }}>{date}</div><div style={{ fontSize: 11, color: '#8c8c8c' }}>{time}</div></div>;
      },
    },
    {
      title: 'Rating', dataIndex: 'rating', key: 'rating', width: 140,
      render: (v: number) => <Rate disabled defaultValue={v} style={{ fontSize: 14 }} />,
    },
    {
      title: 'Helpful', dataIndex: 'helpful', key: 'helpful', width: 80,
      render: (v: boolean) => v ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    { title: 'Feedback', dataIndex: 'feedback_text', key: 'feedback_text', ellipsis: true },
    { title: 'Search ID', dataIndex: 'search_id', key: 'search_id', width: 100, ellipsis: true },
    { title: 'Part ID', dataIndex: 'part_id', key: 'part_id', width: 100, ellipsis: true },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Avatar icon={<StarOutlined />} size="small" style={{ backgroundColor: '#faad14', marginBottom: 4 }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Total Feedback</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{(feedbackStats?.total_feedback || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Average Rating</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>
              {typeof feedbackStats?.average_rating === 'number' ? feedbackStats.average_rating.toFixed(1) : 'N/A'}
            </Title>
            {typeof feedbackStats?.average_rating === 'number' && (
              <Rate disabled defaultValue={Math.round(feedbackStats.average_rating)} style={{ fontSize: 14 }} />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Avatar icon={<LikeOutlined />} size="small" style={{ backgroundColor: '#52c41a', marginBottom: 4 }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Helpful Count</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{(feedbackStats?.helpful_count || 0).toLocaleString()}</Title>
          </Card>
        </Col>
      </Row>

      {ratingDistribution.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <SimplePieChart data={ratingDistribution} dataKey="value" title="Rating Distribution" height={280} />
        </Card>
      )}

      <Card size="small" title="Feedback Entries" extra={
        <Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(feedbackList, 'feedback')}>Export</Button>
      }>
        <Table
          columns={feedbackColumns}
          dataSource={feedbackList}
          rowKey={(r, i) => r.id || `fb-${i}`}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
          }}
          locale={{ emptyText: <EmptyState /> }}
        />
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Billing Tab - API Call Counts per Tenant
// ═══════════════════════════════════════════════════════════════════
function BillingTab({ startDate, endDate, tenantId, applicationId, onLastUpdated }: TabFilterProps) {
  const [billingData, setBillingData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [granularity, setGranularity] = useState<string>('daily');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const loadBilling = useCallback(async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString().split('T')[0]);
      if (endDate) params.append('end_date', endDate.toISOString().split('T')[0]);
      if (tenantId) params.append('tenant_id', tenantId);
      if (applicationId) params.append('application_id', applicationId);
      params.append('granularity', granularity);

      const response = await adminFetch(`${getBackendUrl()}/api/search/admin/billing/summary?${params}`);
      if (response.ok) {
        const data = await response.json();
        setBillingData(data.data || null);
        onLastUpdated(new Date());
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setError(`Failed to load billing data: ${errorData.message || errorData.detail || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(`Error: ${err.message || 'Failed to load billing data'}`);
    }
  }, [startDate, endDate, tenantId, applicationId, granularity, onLastUpdated]);

  useEffect(() => {
    loadBilling();
    const interval = setInterval(loadBilling, 30000);
    return () => clearInterval(interval);
  }, [loadBilling]);

  const callTypeLabels: Record<string, string> = {
    gemini_calls: 'Gemini NLP',
    gemini_vision_calls: 'Gemini Vision',
    whisper_calls: 'Whisper ASR',
    vahan_calls: 'Vahan API',
    google_search_calls: 'Google Search',
    vector_search_calls: 'Embedding & Vector Search',
    tvs_api_calls: 'TVS Catalog',
    boodmo_api_calls: 'Boodmo Catalog',
    gemini_smart_calls: 'Gemini Smart Search',
    image_processing_calls: 'Image Processing',
    total_external_calls: 'Total API Calls',
  };

  const totals = billingData?.totals || {};

  // Build chart data from totals (exclude total_external_calls and searches)
  const callBreakdownData = Object.entries(totals)
    .filter(([key, val]) => key !== 'total_external_calls' && key !== 'searches' && (val as number) > 0)
    .map(([key, val]) => ({
      name: callTypeLabels[key] || key,
      value: val as number,
    }));

  // Breakdown table data
  const breakdownColumns: TableColumnsType<any> = [
    { title: 'Period', dataIndex: 'period', key: 'period', width: 120 },
    { title: 'Searches', dataIndex: 'searches', key: 'searches', width: 100, sorter: (a: any, b: any) => (a.searches || 0) - (b.searches || 0) },
    { title: 'Total API Calls', dataIndex: 'total_api_calls', key: 'total_api_calls', width: 130, sorter: (a: any, b: any) => (a.total_api_calls || 0) - (b.total_api_calls || 0), render: (v: number) => <Text strong>{v || 0}</Text> },
    { title: 'Gemini NLP', dataIndex: 'gemini_calls', key: 'gemini_calls', width: 110 },
    { title: 'Gemini Vision', dataIndex: 'gemini_vision_calls', key: 'gemini_vision_calls', width: 120 },
    { title: 'Whisper ASR', dataIndex: 'whisper_calls', key: 'whisper_calls', width: 110 },
    { title: 'TVS Catalog', dataIndex: 'tvs_api_calls', key: 'tvs_api_calls', width: 110 },
    { title: 'Boodmo', dataIndex: 'boodmo_api_calls', key: 'boodmo_api_calls', width: 100 },
  ];

  // Trend line data
  const trendData = (billingData?.breakdown || []).map((row: any) => ({
    name: row.period,
    'API Calls': row.total_api_calls || 0,
    Searches: row.searches || 0,
  }));

  return (
    <div>
      {error && <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 16 }} showIcon />}

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Searches</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{(totals.searches || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total API Calls</Text>
            <Title level={3} style={{ margin: '4px 0 0', color: '#1890ff' }}>{(totals.total_external_calls || 0).toLocaleString()}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Avg Calls/Search</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>
              {totals.searches > 0 ? (totals.total_external_calls / totals.searches).toFixed(1) : '0'}
            </Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: statCardBodyStyle }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Gemini Calls</Text>
            <Title level={3} style={{ margin: '4px 0 0', color: '#722ed1' }}>
              {((totals.gemini_calls || 0) + (totals.gemini_vision_calls || 0)).toLocaleString()}
            </Title>
          </Card>
        </Col>
      </Row>

      {/* Call Type Breakdown Cards - External Billable */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>External Billable</Title>
          <Tag color="red">Paid APIs</Tag>
        </Flex>
        <Row gutter={[12, 12]}>
          {Object.entries(totals)
            .filter(([key]) => key !== 'total_external_calls' && key !== 'searches' && !(billingData?.non_billable_types || []).includes(key))
            .map(([key, val]) => (
              <Col xs={12} sm={8} md={6} lg={4} key={key}>
                <Card size="small" styles={{ body: { padding: '10px 14px', textAlign: 'center' } }}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{callTypeLabels[key] || key}</Text>
                  <Title level={4} style={{ margin: '4px 0 0', color: (val as number) > 0 ? '#f5222d' : '#d9d9d9' }}>
                    {((val as number) || 0).toLocaleString()}
                  </Title>
                </Card>
              </Col>
            ))}
        </Row>
      </Card>

      {/* Call Type Breakdown Cards - Non-Billable */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>Non-Billable</Title>
          <Tag color="green">Internal</Tag>
        </Flex>
        <Row gutter={[12, 12]}>
          {Object.entries(totals)
            .filter(([key]) => (billingData?.non_billable_types || []).includes(key))
            .map(([key, val]) => (
              <Col xs={12} sm={8} md={6} lg={4} key={key}>
                <Card size="small" styles={{ body: { padding: '10px 14px', textAlign: 'center' } }}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{callTypeLabels[key] || key}</Text>
                  <Title level={4} style={{ margin: '4px 0 0', color: (val as number) > 0 ? '#52c41a' : '#d9d9d9' }}>
                    {((val as number) || 0).toLocaleString()}
                  </Title>
                </Card>
              </Col>
            ))}
        </Row>
      </Card>

      {/* Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small">
            {trendData.length > 0
              ? <SimpleBarChart data={trendData} dataKey="API Calls" title={`API Calls Over Time (${granularity})`} height={300} />
              : <EmptyState />}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small">
            {callBreakdownData.length > 0
              ? <SimplePieChart data={callBreakdownData} dataKey="value" title="API Calls by Type" height={300} />
              : <EmptyState />}
          </Card>
        </Col>
      </Row>

      {/* Breakdown Table */}
      <Card size="small" title="Period Breakdown" extra={
        <Flex gap={8} align="center">
          <Select
            size="small"
            value={granularity}
            onChange={(v) => setGranularity(v)}
            style={{ width: 120 }}
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Monthly', value: 'monthly' },
            ]}
          />
          <Button size="small" icon={<DownloadOutlined />} onClick={() => exportToCSV(billingData?.breakdown || [], 'billing-breakdown')}>Export</Button>
        </Flex>
      }>
        <Table
          columns={breakdownColumns}
          dataSource={billingData?.breakdown || []}
          rowKey={(r, i) => `${r.period}-${i}`}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          locale={{ emptyText: <EmptyState /> }}
        />
      </Card>
    </div>
  );
}

export default Analytics;
